import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Spinner,
  Badge,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Search24Regular, Receipt24Regular } from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from './DataTable';
import { useSectionStyles } from './customerForm/styles';

export interface OrdersPanelOrder {
  id: string;
  number: number;
  status: string;
  starts_at: string;
  stops_at: string;
  /** ISO timestamp when the order row was created in TWM. Optional so older
   *  callers/stubs that don't populate it still type-check. */
  created_at?: string;
  /** ISO timestamp of the last *manual* edit by a user (staff or customer) —
   *  NOT bumped by background syncs, unlike updated_at. Null/absent until the
   *  order's first in-app edit. Drives the Last modified column. */
  last_edited_at?: string;
  grand_total_in_cents: number;
  payment_status: string;
  /** Set on Company profiles where orders are aggregated from the
   *  company itself + every linked Contact — distinguishes who placed
   *  each order. Undefined on Individual profiles. */
  placedByName?: string;
  placedById?: string;
}

interface OrdersPanelProps {
  orders: OrdersPanelOrder[];
  loading: boolean;
  error: string | null;
  /** Customer whose profile this panel is on. Used to distinguish
   *  "Direct" (company-placed) rows from contact-placed rows in the
   *  Placed by column. */
  customerId?: string;
  /** Drives the Placed-by column visibility. Only Companies show it. */
  customerKind?: 'individual' | 'contact' | 'company';
  /** When true, render a section title above the table. Default true.
   *  Surfaces that already have their own framing chrome (a parent
   *  card, a tab strip, etc.) can pass false. */
  showTitle?: boolean;
}

const useLocalStyles = makeStyles({
  orderNumberLink: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    '&:hover': {
      textDecorationLine: 'underline',
    },
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: 500,
    ...shorthands.padding('2px', '8px'),
    ...shorthands.borderRadius('4px'),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    ...shorthands.gap('12px'),
  },
  emptyStateIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: '40px',
  },
  emptyStateText: {
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
  },
  errorBlock: {
    ...shorthands.padding('20px'),
    backgroundColor: '#fef0f0',
    ...shorthands.border('1px', 'solid', '#fcd4d4'),
    ...shorthands.borderRadius('4px'),
    color: '#c42b1c',
  },
});

const formatDateTime = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatPrice = (priceInCents: number) =>
  `$${(priceInCents / 100).toFixed(2)}`;

const getOrderStatusStyle = (status: string): React.CSSProperties => {
  switch (status) {
    case 'draft': return { backgroundColor: '#f3f2f1', color: '#605e5c' };
    case 'reserved': return { backgroundColor: '#cfe4ff', color: '#0078d4' };
    case 'picked_up': return { backgroundColor: '#d3f3e9', color: '#107c41' };
    case 'returned': return { backgroundColor: '#e9e3f7', color: '#5c2d91' };
    case 'archived': return { backgroundColor: '#edebe9', color: '#8a8886' };
    case 'canceled': return { backgroundColor: '#fde7e9', color: '#d13438' };
    default: return { backgroundColor: '#f3f2f1', color: '#605e5c' };
  }
};

const getOrderStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    draft: 'Draft', reserved: 'Reserved',
    picked_up: 'Started', returned: 'Completed',
    archived: 'Archived', canceled: 'Canceled',
  };
  return labels[status] || status.charAt(0).toUpperCase() + status.slice(1);
};

const getPaymentStatusBadge = (paymentStatus: string) => {
  const lower = paymentStatus.toLowerCase();
  if (lower === 'paid' || lower === 'payment_paid') {
    return { color: 'success' as const, style: { backgroundColor: '#d3f3e9', color: '#107c41' }, label: 'Paid' };
  }
  if (lower === 'partially_paid') {
    return { color: 'informative' as const, style: { backgroundColor: '#cfe4ff', color: '#0078d4' }, label: 'Partially Paid' };
  }
  if (lower === 'payment_due') {
    return { color: 'warning' as const, style: { backgroundColor: '#fff4e6', color: '#d87a16' }, label: 'Payment Due' };
  }
  if (lower === 'overpaid') {
    return { color: 'danger' as const, style: { backgroundColor: '#fde7e9', color: '#d13438' }, label: 'Overpaid' };
  }
  return { color: 'subtle' as const, style: { backgroundColor: '#8c8c8c' }, label: paymentStatus };
};

/**
 * Reusable orders table for customer surfaces. Used on the customer
 * profile page (currently the only consumer) but designed to be
 * dropped into other contexts — e.g. dashboards, related-customer
 * panels — without bringing the page's other concerns along.
 *
 * `showTitle` controls whether a section header ("Orders" + receipt
 * icon, matching the General/Address/Tags pattern) renders above the
 * table. Hide it when the surrounding chrome already provides framing.
 */
const OrdersPanel: React.FC<OrdersPanelProps> = ({
  orders,
  loading,
  error,
  customerId,
  customerKind,
  showTitle = true,
}) => {
  const sectionStyles = useSectionStyles();
  const styles = useLocalStyles();
  const navigate = useNavigate();

  const columns: ColumnConfig<OrdersPanelOrder>[] = [
    {
      columnKey: 'number',
      label: 'Order #',
      sortable: true,
      groupable: false,
      filterable: false,
      width: '100px',
      render: (order) => (
        <span
          className={styles.orderNumberLink}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/orders/${order.id}`);
          }}
        >
          #{order.number}
        </span>
      ),
    },
    {
      columnKey: 'status',
      label: 'Status',
      sortable: true,
      groupable: true,
      filterable: false,
      width: '120px',
      render: (order) => (
        <span className={styles.statusBadge} style={getOrderStatusStyle(order.status)}>
          {getOrderStatusLabel(order.status)}
        </span>
      ),
    },
    ...(customerKind === 'company'
      ? [
          {
            columnKey: 'placedByName',
            label: 'Placed by',
            sortable: true,
            groupable: true,
            filterable: false,
            width: '160px',
            render: (order: OrdersPanelOrder) =>
              order.placedById && order.placedById !== customerId ? (
                <span
                  className={styles.orderNumberLink}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/customers/${order.placedById}`);
                  }}
                >
                  {order.placedByName}
                </span>
              ) : (
                <span style={{ color: tokens.colorNeutralForeground3 }}>
                  {order.placedByName || 'Direct'}
                </span>
              ),
          } as ColumnConfig<OrdersPanelOrder>,
        ]
      : []),
    {
      columnKey: 'created_at',
      label: 'Created',
      sortable: true,
      groupable: false,
      filterable: false,
      width: '120px',
      render: (order) => formatDateTime(order.created_at || ''),
    },
    {
      columnKey: 'starts_at',
      label: 'Pick up',
      sortable: true,
      groupable: false,
      filterable: false,
      width: '120px',
      render: (order) => formatDateTime(order.starts_at),
    },
    {
      columnKey: 'stops_at',
      label: 'Return',
      sortable: true,
      groupable: false,
      filterable: false,
      width: '120px',
      render: (order) => formatDateTime(order.stops_at),
    },
    {
      columnKey: 'last_edited_at',
      label: 'Last modified',
      sortable: true,
      groupable: false,
      filterable: false,
      width: '130px',
      // Em dash when never edited in-app (null) so the column reads as
      // "no manual edits yet" rather than a blank that looks like a bug.
      render: (order) => order.last_edited_at ? formatDateTime(order.last_edited_at) : '—',
    },
    {
      columnKey: 'grand_total_in_cents',
      label: 'Price',
      sortable: true,
      groupable: false,
      filterable: false,
      width: '110px',
      render: (order) => formatPrice(order.grand_total_in_cents),
    },
    {
      columnKey: 'payment_status',
      label: 'Payment status',
      sortable: true,
      groupable: true,
      filterable: false,
      width: '160px',
      render: (order) => {
        const badge = getPaymentStatusBadge(order.payment_status);
        return (
          <Badge appearance="filled" color={badge.color} style={badge.style}>
            {badge.label}
          </Badge>
        );
      },
    },
  ];

  return (
    <>
      {showTitle && (
        <div className={sectionStyles.sectionTitle}>
          <Receipt24Regular />
          Orders
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Spinner size="medium" label="Loading orders..." />
        </div>
      )}

      {error && (
        <div className={styles.errorBlock}>Error: {error}</div>
      )}

      {!loading && !error && orders.length === 0 && (
        <div className={styles.emptyState}>
          <Search24Regular className={styles.emptyStateIcon} />
          <div className={styles.emptyStateText}>No orders found for this customer</div>
        </div>
      )}

      {!loading && !error && orders.length > 0 && (
        <DataTable<OrdersPanelOrder>
          data={orders}
          columns={columns}
          keyField="id"
          hideableColumns
          columnVisibilityStorageKey="orders_panel"
          itemsPerPage={25}
          primaryColumnKey="number"
          disableWrapper={true}
          defaultSortColumn="last_edited_at"
          defaultSortDirection="descending"
        />
      )}
    </>
  );
};

export default OrdersPanel;
